import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useNotification } from './Notification';
import './TagSelector.css';

function TagSelector({ tenantId, selectedTags = [], onChange, multiple = true }) {
  const { showNotification } = useNotification();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#FF6B6B');
  const containerRef = useRef(null);

  useEffect(() => {
    if (tenantId) {
      loadTags();
    }
  }, [tenantId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowCreateForm(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(data);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tagId) => {
    if (multiple) {
      const newSelected = selectedTags.includes(tagId)
        ? selectedTags.filter(id => id !== tagId)
        : [...selectedTags, tagId];
      onChange(newSelected);
    } else {
      onChange(selectedTags.includes(tagId) ? [] : [tagId]);
      setShowDropdown(false);
    }
  };

  const removeTag = (tagId) => {
    const newSelected = selectedTags.filter(id => id !== tagId);
    onChange(newSelected);
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!newTagName.trim()) {
      showNotification('Bitte geben Sie einen Tag-Namen ein', 'error');
      return;
    }
    try {
      const newTag = await api.post('/tags', { name: newTagName.trim(), color: newTagColor }, tenantId);
      setTags([...tags, newTag]);
      setNewTagName('');
      setNewTagColor('#FF6B6B');
      setShowCreateForm(false);
      const newSelected = [...selectedTags, newTag.id];
      onChange(newSelected);
      showNotification('Tag erfolgreich erstellt', 'success');
    } catch (error) {
      console.error('Error creating tag:', error);
      showNotification(error.message || 'Fehler beim Erstellen des Tags', 'error');
    }
  };

  const presetColors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#FFD93D',
    '#C77DFF', '#6BCF7F', '#FF8C94', '#9B8DFF', '#FFB84D'
  ];

  const selectedTagObjects = tags.filter(tag => selectedTags.includes(tag.id));
  const availableTags = tags.filter(tag => !selectedTags.includes(tag.id));

  if (loading) {
    return <div className="tag-selector-loading">Lade Tags...</div>;
  }

  return (
    <div className="tag-selector" ref={containerRef}>
      <label className="tag-selector-label">Tags</label>
      <div className="tag-selector-container">
        <div className="selected-tags">
          {selectedTagObjects.map(tag => (
            <span
              key={tag.id}
              className="tag-badge selected"
              style={{ backgroundColor: tag.color }}
              onClick={() => removeTag(tag.id)}
              title="Klicken zum Entfernen"
            >
              {tag.name}
            </span>
          ))}
          <button
            type="button"
            className="tag-selector-toggle"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            + Tag hinzufügen
          </button>
        </div>
        {showDropdown && (
          <div className="tag-dropdown" onClick={(e) => e.stopPropagation()}>
            {showCreateForm ? (
              <div className="tag-create-form" onClick={(e) => e.stopPropagation()}>
                <form onSubmit={handleCreateTag}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Tag-Name"
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <input
                      type="color"
                      value={newTagColor}
                      onChange={(e) => setNewTagColor(e.target.value)}
                      style={{
                        width: '40px',
                        height: '40px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    {presetColors.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setNewTagColor(color);
                        }}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          border: `2px solid ${newTagColor === color ? '#333' : '#ddd'}`,
                          backgroundColor: color,
                          cursor: 'pointer'
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="submit"
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Erstellen
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCreateForm(false);
                        setNewTagName('');
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#f5f5f5',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Abbrechen
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <>
                <div
                  className="tag-dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCreateForm(true);
                  }}
                  style={{ borderBottom: '1px solid #eee', fontWeight: 500 }}
                >
                  + Neuen Tag erstellen
                </div>
                {availableTags.length === 0 ? (
                  <div className="tag-dropdown-empty">Keine weiteren Tags verfügbar</div>
                ) : (
                  availableTags.map(tag => (
                    <div
                      key={tag.id}
                      className="tag-dropdown-item"
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      <span
                        className="tag-badge"
                        style={{ backgroundColor: tag.color }}
                      >
                        {tag.name}
                      </span>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TagSelector;

